import { useState, useCallback } from 'react'
import { useApproximateLocation } from './useApproximateLocation'

export interface UserLocation {
  latitude: number
  longitude: number
  accuracy?: number
}

export interface UseUserLocationReturn {
  location: UserLocation | null
  isLoading: boolean
  error: string | null
  hasPermission: boolean | null
  approximateLocation: { latitude: number; longitude: number } | null
  requestLocation: () => void
  clearLocation: () => void
}

/**
 * Hook to handle user geolocation with permission handling
 * Falls back to IP-based approximate location if geolocation is denied
 */
export function useUserLocation(): UseUserLocationReturn {
  const [location, setLocation] = useState<UserLocation | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [hasPermission, setHasPermission] = useState<boolean | null>(null)
  const approximateLocationHook = useApproximateLocation()

  const requestLocation = useCallback(async () => {
    if (!navigator.geolocation) {
      setError('Geolocation is not supported by your browser')
      setHasPermission(false)
      // Fallback to IP geolocation
      if (!approximateLocationHook.location) {
        approximateLocationHook.requestLocation()
      }
      return
    }

    setIsLoading(true)
    setError(null)

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy
        })
        setHasPermission(true)
        setIsLoading(false)
      },
      async (err) => {
        setError(err.message)
        setHasPermission(false)
        setIsLoading(false)
        // Fallback to IP geolocation if permission denied
        if (!approximateLocationHook.location && !approximateLocationHook.isLoading) {
          await approximateLocationHook.requestLocation()
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 60000 // Cache for 1 minute
      }
    )
  }, [approximateLocationHook])

  const clearLocation = useCallback(() => {
    setLocation(null)
    setError(null)
    setHasPermission(null)
  }, [])

  // Get approximate location (either from IP or fallback)
  const approximateLocation = approximateLocationHook.location
    ? {
        latitude: approximateLocationHook.location.latitude,
        longitude: approximateLocationHook.location.longitude
      }
    : null

  return {
    location,
    isLoading: isLoading || approximateLocationHook.isLoading,
    error,
    hasPermission,
    approximateLocation,
    requestLocation,
    clearLocation
  }
}

